"use client";

import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCart, useUI } from "@/lib/store";
import { boutiques, forfaits, formatPrice, mondes, merveilles, universTech, Product } from "@/lib/data";
import { toast } from "@/hooks/use-toast";
import {
  ShoppingBag,
  Crown,
  Check,
  X,
  ArrowRight,
  Star,
  Phone,
  MapPin,
  User,
  Building2,
  Mail,
  MessageSquare,
  Sparkles,
  Wifi,
  CheckCircle2,
  Loader2,
} from "lucide-react";

/* ═══════════ Carte produit réutilisable ═══════════ */
function BoutiqueProductCard({ p }: { p: Product }) {
  const addItem = useCart((s) => s.addItem);
  const discount = p.oldPrice ? Math.round((1 - p.price / p.oldPrice) * 100) : 0;
  return (
    <div className="group bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl hover:border-[#006B3F]/30 transition-all duration-300 flex flex-col">
      <div className="relative aspect-square overflow-hidden bg-gray-50">
        <Image
          src={p.image}
          alt={p.name}
          fill
          className="object-cover group-hover:scale-105 transition-transform duration-500"
          sizes="(max-width: 768px) 50vw, 25vw"
        />
        {p.badge && (
          <span className="absolute top-2.5 left-2.5 px-2.5 py-0.5 rounded-full text-[9px] font-black tracking-wider bg-gradient-to-r from-[#006B3F] to-[#008751] text-white">
            {p.badge}
          </span>
        )}
        {discount > 0 && (
          <span className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-full bg-[#CE1126] text-white text-[10px] font-black">
            -{discount}%
          </span>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <p className="text-[9px] font-bold tracking-wider text-gray-400 uppercase">{p.category}</p>
        <h4 className="font-bold text-gray-900 text-sm mt-0.5 leading-snug">{p.name}</h4>
        {p.rating && (
          <div className="flex items-center gap-1 mt-1">
            <Star className="w-3 h-3 fill-[#FFD700] text-[#FFD700]" />
            <span className="text-[11px] font-bold text-gray-600">{p.rating}</span>
            <span className="text-[10px] text-gray-400">({p.reviews})</span>
          </div>
        )}
        <div className="mt-auto pt-3 flex items-end justify-between gap-2">
          <div>
            <p className="text-sm font-black text-[#006B3F] leading-none">
              {formatPrice(p.price)} <span className="text-[9px] text-gray-400 font-bold">F</span>
            </p>
            {p.oldPrice && (
              <p className="text-[10px] text-gray-400 line-through mt-0.5">{formatPrice(p.oldPrice)}</p>
            )}
          </div>
          <Button
            size="sm"
            onClick={() =>
              addItem({
                id: p.id,
                name: p.name,
                price: p.price,
                image: p.image,
                presidential: p.price >= 7000000,
              })
            }
            className="bg-gradient-to-r from-[#006B3F] to-[#008751] text-white h-8 px-3 text-xs shrink-0"
          >
            <ShoppingBag className="w-3 h-3 mr-1" /> Acheter
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════ BOUTIQUE DES 5 MONDES ═══════════ */
export function BoutiqueDialog() {
  const { boutiqueMonde, closeBoutique } = useUI();
  const monde = mondes.find((m) => m.id === boutiqueMonde);
  const [filtre, setFiltre] = useState<string>("tous");

  useEffect(() => {
    setFiltre("tous");
  }, [boutiqueMonde]);

  const sourceAll = useMemo<Product[]>(() => {
    if (!boutiqueMonde) return [];
    if (boutiqueMonde === "tout") return Object.values(boutiques).flat();
    if (boutiqueMonde === "tech") return universTech;
    return boutiques[boutiqueMonde] ?? [];
  }, [boutiqueMonde]);

  const produits = useMemo<Product[]>(() => {
    if (!boutiqueMonde) return [];
    return filtre === "tous" ? sourceAll : sourceAll.filter((p) => p.category === filtre);
  }, [sourceAll, filtre]);

  const categories = useMemo<string[]>(() => {
    if (!boutiqueMonde) return [];
    return Array.from(new Set(sourceAll.map((p) => p.category)));
  }, [sourceAll]);

  const title =
    boutiqueMonde === "tout"
      ? "Boutique Complète"
      : boutiqueMonde === "tech"
        ? "Univers Tech"
        : monde?.name ?? "";
  const subtitle =
    boutiqueMonde === "tout"
      ? "Toute la technologie d'exception réunie"
      : boutiqueMonde === "tech"
        ? "240 fiches technologiques au gabarit d'excellence STS"
        : monde?.tagline ?? "";

  return (
    <Dialog open={!!boutiqueMonde} onOpenChange={(o) => !o && closeBoutique()}>
      <DialogContent className="max-w-[95vw] sm:max-w-5xl lg:max-w-6xl max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
        {/* En-tête boutique */}
        <div className="relative bg-[#04140D] text-white px-6 py-5 shrink-0 overflow-hidden">
          <div
            className="absolute inset-0 opacity-40"
            style={{
              background:
                "radial-gradient(ellipse 60% 80% at 85% 20%, rgba(0,107,63,0.5), transparent), radial-gradient(ellipse 40% 60% at 10% 100%, rgba(255,215,0,0.15), transparent)",
            }}
          />
          <div className="relative flex items-start justify-between gap-4">
            <div>
              <p className="text-[10px] font-black tracking-[0.3em] text-[#FFD700] uppercase">
                {boutiqueMonde === "tout" ? "Collection Intégrale" : boutiqueMonde === "tech" ? "Technologie de Pointe" : "Boutique d'Exception"}
              </p>
              <DialogHeader>
                <DialogTitle className="text-2xl font-black text-white mt-1">
                  {title}
                </DialogTitle>
                <DialogDescription className="text-white/60 text-sm mt-0.5">
                  {subtitle} · {produits.length} produit{produits.length > 1 ? "s" : ""} d&apos;exception
                </DialogDescription>
              </DialogHeader>
            </div>
            <div className="hidden sm:block w-20 h-14 rounded-xl overflow-hidden border border-[#FFD700]/30 shrink-0">
              {monde ? (
                <Image
                  src={monde.image}
                  alt={monde.name}
                  width={80}
                  height={56}
                  className="object-cover w-full h-full"
                />
              ) : boutiqueMonde === "tech" && universTech[0] ? (
                <Image
                  src={universTech[0].image}
                  alt="Univers Tech"
                  width={80}
                  height={56}
                  className="object-cover w-full h-full"
                />
              ) : null}
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="flex flex-wrap gap-2 px-6 py-3 border-b border-gray-100 bg-white shrink-0">
          <button
            onClick={() => setFiltre("tous")}
            className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors ${
              filtre === "tous"
                ? "bg-[#006B3F] text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            Tous ({sourceAll.length})
          </button>
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setFiltre(c)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors ${
                filtre === c ? "bg-[#006B3F] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Grille produits */}
        <div className="flex-1 overflow-y-auto p-6 bg-gradient-to-b from-[#F5FBF7] to-white">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {produits.map((p) => (
              <BoutiqueProductCard key={p.id} p={p} />
            ))}
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[11px] text-gray-400">
            <span className="inline-flex items-center gap-1.5">
              <Check className="w-3 h-3 text-[#006B3F]" /> Garantie 3 ans
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Check className="w-3 h-3 text-[#006B3F]" /> Livraison Concierge Blanche
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Check className="w-3 h-3 text-[#006B3F]" /> Paiement 6x sans frais
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════ MERVEILLE (Explorer) ═══════════ */
export function MerveilleDialog() {
  const { merveilleId, closeMerveille } = useUI();
  const m = merveilles.find((w) => w.id === merveilleId);

  return (
    <Dialog open={!!merveilleId} onOpenChange={(o) => !o && closeMerveille()}>
      <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[92vh] overflow-y-auto p-0 gap-0">
        {m && (
          <>
            <div className="relative aspect-[16/8] overflow-hidden bg-[#04140D]">
              <Image src={m.image} alt={m.name} fill className="object-cover" sizes="700px" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#04140D] via-[#04140D]/30 to-transparent" />
              <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-[10px] font-black tracking-widest bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black shadow-lg">
                {m.badge}
              </span>
              <div className="absolute inset-x-0 bottom-0 p-6">
                <p className="text-[10px] font-bold tracking-[0.25em] text-[#00B86B] uppercase">
                  {m.category}
                </p>
                <DialogHeader>
                  <DialogTitle className="text-2xl font-black text-white mt-1">{m.name}</DialogTitle>
                  <DialogDescription className="text-[#FFD700] text-sm font-semibold">
                    {m.tagline}
                  </DialogDescription>
                </DialogHeader>
              </div>
            </div>
            <div className="p-6 bg-gradient-to-b from-white to-[#F5FBF7]">
              <p className="text-xs font-black tracking-[0.2em] text-gray-500 uppercase mb-4">
                La sélection de cette Merveille
              </p>
              <div className="space-y-3">
                {m.products.map((p, idx) => (
                  <div
                    key={p.name}
                    className="flex items-center justify-between gap-3 p-4 rounded-2xl bg-white border border-gray-100 shadow-sm"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FFD700] to-[#FFA500] text-black text-xs font-black flex items-center justify-center shrink-0">
                        {idx + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="font-bold text-gray-900 text-sm truncate">{p.name}</p>
                        <p className="text-[#006B3F] font-black text-sm">
                          {formatPrice(p.price)} FCFA
                        </p>
                      </div>
                    </div>
                    <BoutiqueBuyButton name={p.name} price={p.price} image={m.image} />
                  </div>
                ))}
              </div>
              <div className="mt-5 rounded-2xl bg-[#04140D] p-4 text-white/70 text-xs leading-relaxed">
                <span className="text-[#FFD700] font-bold inline-flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> Expérience Merveille incluse :
                </span>{" "}
                démonstration privée sur rendez-vous, installation concierge et garantie premium 3
                ans offerte sur toute la sélection.
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* Bouton achat compact (utilisé dans MerveilleDialog) */
function BoutiqueBuyButton({ name, price, image }: { name: string; price: number; image: string }) {
  const addItem = useCart((s) => s.addItem);
  return (
    <Button
      size="sm"
      onClick={() =>
        addItem({
          id: `mer-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
          name,
          price,
          image,
        })
      }
      className="bg-gradient-to-r from-[#FFD700] to-[#DAA520] text-black font-black h-8 px-3 text-xs shrink-0 hover:shadow-[0_0_20px_rgba(255,215,0,0.4)]"
    >
      <ShoppingBag className="w-3 h-3 mr-1" /> Acheter
    </Button>
  );
}

/* ═══════════ COMPARATEUR DE FORFAITS ═══════════ */
export function ForfaitsDialog() {
  const { forfaitsOpen, closeForfaits, openBoutique } = useUI();

  const souscrire = (f: (typeof forfaits)[number]) => {
    const addItem = useCart.getState().addItem;
    addItem({
      id: f.id,
      name: `Forfait ${f.name} (mensuel)`,
      price: f.price,
      image: "/images/products/routeur-wifi7.png",
      presidential: f.presidential,
    });
    toast({
      title: f.presidential ? "👑 Forfait Souverain sélectionné" : "✓ Forfait sélectionné",
      description: `${f.name} — activation par un conseiller sous 2h`,
    });
    closeForfaits();
    openBoutique("mobile");
  };

  return (
    <Dialog open={forfaitsOpen} onOpenChange={(o) => !o && closeForfaits()}>
      <DialogContent className="max-w-[95vw] sm:max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <p className="text-[10px] font-black tracking-[0.3em] text-[#FFD700] uppercase">
            Forfaits 5G Teranga
          </p>
          <DialogTitle className="text-2xl font-black text-gray-900">
            Comparer les <span className="text-[#006B3F]">Forfaits</span>
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            Quatre formules d&apos;exception, du 5G Essentiel au réseau souverain chiffré. Sans
            engagement, activation sous 2 heures.
          </DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-2">
          {forfaits.map((f) => (
            <div
              key={f.id}
              className={`relative rounded-3xl p-5 flex flex-col transition-all hover:-translate-y-1 ${
                f.presidential
                  ? "bg-gradient-to-b from-[#171106] to-[#0B0804] border-2 border-[#FFD700]/50 text-white"
                  : f.popular
                    ? "bg-gradient-to-b from-[#F5FBF7] to-white border-2 border-[#006B3F] shadow-xl"
                    : "bg-white border border-gray-200 shadow-sm"
              }`}
            >
              {f.popular && !f.presidential && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-[#006B3F] text-white text-[9px] font-black tracking-widest whitespace-nowrap">
                  LE PLUS CHOISI
                </span>
              )}
              {f.presidential && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-gradient-to-r from-[#FFD700] to-[#DAA520] text-black text-[9px] font-black tracking-widest whitespace-nowrap inline-flex items-center gap-1">
                  <Crown className="w-2.5 h-2.5" /> SOUVERAIN
                </span>
              )}
              <h4 className={`font-black ${f.presidential ? "text-[#FFD700]" : "text-gray-900"}`}>
                {f.name}
              </h4>
              <p className={`text-[10px] mt-0.5 ${f.presidential ? "text-white/50" : "text-gray-400"}`}>
                {f.speed}
              </p>
              <p className={`mt-3 font-black text-xl ${f.presidential ? "text-[#FFD700]" : "text-[#006B3F]"}`}>
                {formatPrice(f.price)}
                <span className="text-[10px] font-bold text-gray-400"> F/mois</span>
              </p>
              <p className={`text-[11px] font-semibold mt-1 ${f.presidential ? "text-white/70" : "text-gray-600"}`}>
                {f.data}
              </p>
              <ul className="mt-4 space-y-2 flex-1">
                {f.features.map((feat) => (
                  <li
                    key={feat}
                    className={`flex items-start gap-1.5 text-[11px] leading-snug ${
                      f.presidential ? "text-white/75" : "text-gray-600"
                    }`}
                  >
                    <Check
                      className={`w-3.5 h-3.5 mt-px shrink-0 ${
                        f.presidential ? "text-[#FFD700]" : "text-[#008751]"
                      }`}
                    />
                    {feat}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => souscrire(f)}
                className={`mt-4 w-full h-9 text-xs font-black ${
                  f.presidential
                    ? "bg-gradient-to-r from-[#FFD700] to-[#DAA520] text-black"
                    : f.popular
                      ? "bg-gradient-to-r from-[#006B3F] to-[#008751] text-white"
                      : "bg-gray-900 text-white hover:bg-gray-800"
                }`}
              >
                Souscrire <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-center text-gray-400 mt-3">
          Sans engagement · Résiliable à tout moment · Activation e-SIM gratuite
        </p>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════ CHECKOUT « COMMANDER AVEC CONCIERGE » ═══════════ */
type OrderStep = "form" | "processing" | "success";

export function CheckoutDialog() {
  const { checkoutOpen, closeCheckout } = useUI();
  const { items, total, clearCart, isOpen } = useCart();
  const [step, setStep] = useState<OrderStep>("form");
  const [orderRef, setOrderRef] = useState("");
  const [form, setForm] = useState({
    nom: "",
    telephone: "",
    adresse: "",
    ville: "Dakar",
    paiement: "Orange Money",
    notes: "",
  });

  // Fermeture synchronisée : si le panier (Sheet) se ferme, on réinitialise le checkout
  useEffect(() => {
    if (!isOpen && !checkoutOpen) {
      const t = setTimeout(() => setStep("form"), 300);
      return () => clearTimeout(t);
    }
  }, [isOpen, checkoutOpen]);

  const canSubmit =
    form.nom.trim().length >= 3 && form.telephone.trim().length >= 9 && form.adresse.trim().length >= 5;

  const commander = () => {
    if (!canSubmit) {
      toast({
        title: "Informations incomplètes",
        description: "Veuillez renseigner votre nom, téléphone et adresse de livraison.",
        variant: "destructive",
      });
      return;
    }
    setStep("processing");
    setTimeout(() => {
      setOrderRef(`STS-${new Date().getFullYear()}-${Math.floor(100000 + Math.random() * 899999)}`);
      setStep("success");
      clearCart();
      toast({
        title: "👑 Commande confirmée",
        description: "Votre concierge dédié vous appelle dans les prochaines minutes.",
      });
    }, 1600);
  };

  const paiementIcons: Record<string, string> = {
    "Orange Money": "🟠",
    Wave: "🌊",
    "Visa / Mastercard": "💳",
    WARI: "🏦",
    "Paiement 6x sans frais": "📅",
    "Espèces à la livraison": "💵",
  };

  return (
    <Dialog
      open={checkoutOpen}
      onOpenChange={(o) => {
        if (!o) {
          closeCheckout();
          if (step === "success") setStep("form");
        }
      }}
    >
      <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[92vh] overflow-y-auto">
        {step === "form" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <Crown className="w-5 h-5 text-[#FFD700]" /> Commander avec Concierge
              </DialogTitle>
              <DialogDescription>
                Un concierge dédié finalise votre commande par téléphone et organise la livraison
                blanche.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-2 rounded-2xl border border-gray-100 bg-[#F5FBF7] p-3 max-h-40 overflow-y-auto">
              {items.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-2">
                  Votre panier est vide — ajoutez des produits avant de commander.
                </p>
              ) : (
                items.map((i) => (
                  <div key={i.id} className="flex justify-between text-xs py-1">
                    <span className="text-gray-600 truncate pr-2">
                      {i.presidential ? "👑 " : ""}
                      {i.name} × {i.quantity}
                    </span>
                    <span className="font-bold text-[#006B3F] whitespace-nowrap">
                      {formatPrice(i.price * i.quantity)} F
                    </span>
                  </div>
                ))
              )}
              {items.length > 0 && (
                <div className="flex justify-between text-sm font-black border-t border-[#006B3F]/10 pt-2 mt-1">
                  <span>Total</span>
                  <span className="text-[#006B3F]">{formatPrice(total())} FCFA</span>
                </div>
              )}
            </div>

            <div className="grid gap-3 mt-2">
              <div className="grid gap-1.5">
                <Label htmlFor="ck-nom" className="text-xs flex items-center gap-1.5">
                  <User className="w-3 h-3" /> Nom complet *
                </Label>
                <Input
                  id="ck-nom"
                  placeholder="Ex : Aïdara Maimouna"
                  value={form.nom}
                  onChange={(e) => setForm({ ...form, nom: e.target.value })}
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="ck-tel" className="text-xs flex items-center gap-1.5">
                    <Phone className="w-3 h-3" /> Téléphone *
                  </Label>
                  <Input
                    id="ck-tel"
                    type="tel"
                    placeholder="+221 77 000 00 00"
                    value={form.telephone}
                    onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label className="text-xs flex items-center gap-1.5">
                    <MapPin className="w-3 h-3" /> Ville
                  </Label>
                  <Select
                    value={form.ville}
                    onValueChange={(v) => setForm({ ...form, ville: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["Dakar", "Thiès", "Saint-Louis", "Ziguinchor", "Kaolack", "Touba", "Autre région"].map(
                        (v) => (
                          <SelectItem key={v} value={v}>
                            {v}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="ck-adresse" className="text-xs flex items-center gap-1.5">
                  <MapPin className="w-3 h-3" /> Adresse de livraison *
                </Label>
                <Input
                  id="ck-adresse"
                  placeholder="Quartier, rue, repère…"
                  value={form.adresse}
                  onChange={(e) => setForm({ ...form, adresse: e.target.value })}
                />
              </div>
              <div className="grid gap-1.5">
                <Label className="text-xs flex items-center gap-1.5">
                  <CheckCircle2 className="w-3 h-3" /> Mode de paiement
                </Label>
                <Select
                  value={form.paiement}
                  onValueChange={(v) => setForm({ ...form, paiement: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(paiementIcons).map((p) => (
                      <SelectItem key={p} value={p}>
                        {paiementIcons[p]} {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="ck-notes" className="text-xs flex items-center gap-1.5">
                  <MessageSquare className="w-3 h-3" /> Instructions au concierge
                </Label>
                <Textarea
                  id="ck-notes"
                  rows={2}
                  placeholder="Créneau souhaité, étage, configuration souhaitée…"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </div>
            </div>

            <Button
              onClick={commander}
              disabled={items.length === 0}
              className="w-full mt-3 h-11 bg-gradient-to-r from-[#006B3F] to-[#008751] text-white font-bold disabled:opacity-50"
            >
              Confirmer la Commande <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <p className="text-[10px] text-center text-gray-400">
              Garantie 3 ans · Livraison Concierge Blanche · Retour 30 jours
            </p>
          </>
        )}

        {step === "processing" && (
          <div className="py-14 flex flex-col items-center text-center gap-4">
            <Loader2 className="w-10 h-10 text-[#006B3F] animate-spin" />
            <div>
              <p className="font-black text-lg text-gray-900">Traitement de votre commande…</p>
              <p className="text-sm text-gray-500 mt-1">
                Attribution de votre concierge personnel en cours.
              </p>
            </div>
          </div>
        )}

        {step === "success" && (
          <div className="py-8 flex flex-col items-center text-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#006B3F] to-[#008751] flex items-center justify-center shadow-xl shadow-[#006B3F]/30">
              <Check className="w-8 h-8 text-[#FFD700]" />
            </div>
            <div>
              <p className="font-black text-xl text-gray-900">Commande Confirmée !</p>
              <p className="text-sm text-gray-500 mt-1 max-w-xs">
                Merci {form.nom.split(" ")[0]} ! Votre concierge dédié vous appelle au{" "}
                <span className="font-bold text-gray-700">{form.telephone}</span> sous 15 minutes.
              </p>
            </div>
            <div className="w-full rounded-2xl border border-[#FFD700]/40 bg-gradient-to-r from-[#FFFBEF] to-[#FFF7DE] p-4">
              <p className="text-[10px] font-black tracking-widest text-[#B8860B] uppercase">
                Référence de commande
              </p>
              <p className="font-black text-2xl text-gray-900 tracking-wide mt-1">{orderRef}</p>
              <p className="text-[11px] text-gray-500 mt-1">
                Livraison {form.ville} · {form.paiement}
              </p>
            </div>
            <Button
              onClick={() => {
                closeCheckout();
                setStep("form");
              }}
              className="w-full bg-gradient-to-r from-[#006B3F] to-[#008751] text-white h-11 font-bold"
            >
              <ShoppingBag className="w-4 h-4 mr-1.5" /> Continuer mes achats
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════ DEVIS SOLUTIONS PRO ═══════════ */
export function DevisDialog() {
  const { devisOpen, closeDevis } = useUI();
  const [envoye, setEnvoye] = useState(false);

  const envoyer = (e: React.FormEvent) => {
    e.preventDefault();
    setEnvoye(true);
    toast({
      title: "✓ Demande de devis envoyée",
      description: "Un expert Solutions Pro vous contacte sous 2 heures ouvrées.",
    });
  };

  return (
    <Dialog
      open={devisOpen}
      onOpenChange={(o) => {
        if (!o) {
          closeDevis();
          setTimeout(() => setEnvoye(false), 300);
        }
      }}
    >
      <DialogContent className="max-w-[95vw] sm:max-w-md">
        {!envoye ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#006B3F]" /> Devis Gratuit Solutions Pro
              </DialogTitle>
              <DialogDescription>
                Réponse d&apos;un expert sous 2h ouvrées. Étude personnalisée sans engagement.
              </DialogDescription>
            </DialogHeader>
            <form className="grid gap-3 mt-1" onSubmit={envoyer}>
              <div className="grid gap-1.5">
                <Label htmlFor="dv-societe" className="text-xs">Entreprise *</Label>
                <Input id="dv-societe" required placeholder="Nom de votre entreprise" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="dv-nom" className="text-xs">Contact *</Label>
                  <Input id="dv-nom" required placeholder="Votre nom" />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="dv-email" className="text-xs flex items-center gap-1.5">
                    <Mail className="w-3 h-3" /> Email
                  </Label>
                  <Input id="dv-email" type="email" placeholder="pro@entreprise.sn" />
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label className="text-xs flex items-center gap-1.5">
                  <Wifi className="w-3 h-3" /> Solution souhaitée
                </Label>
                <Select defaultValue="Solution Pro Complète">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["Solution Pro Complète", "IA Générative & Automatisation", "Cloud Hybride & Data Center", "Cybersécurité 360°", "Fintech & Mobile Money", "IoT & Smart City"].map(
                      (s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="dv-msg" className="text-xs">Votre besoin</Label>
                <Textarea id="dv-msg" rows={3} placeholder="Nombre de postes, délais, site… " />
              </div>
              <Button
                type="submit"
                className="w-full h-11 bg-gradient-to-r from-[#006B3F] to-[#008751] text-white font-bold"
              >
                Recevoir mon devis gratuit <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </form>
          </>
        ) : (
          <div className="py-8 flex flex-col items-center text-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#006B3F] to-[#008751] flex items-center justify-center shadow-lg">
              <Check className="w-7 h-7 text-white" />
            </div>
            <div>
              <p className="font-black text-lg text-gray-900">Demande envoyée !</p>
              <p className="text-sm text-gray-500 mt-1 max-w-xs">
                Un expert certifié analyse votre besoin et vous transmet un devis détaillé sous 2
                heures ouvrées.
              </p>
            </div>
            <Button
              onClick={() => {
                closeDevis();
                setTimeout(() => setEnvoye(false), 300);
              }}
              className="bg-[#006B3F] hover:bg-[#008751] text-white"
            >
              Fermer
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════ REGROUPEMENT DES DIALOGS ═══════════ */
export function SiteDialogs() {
  return (
    <>
      <BoutiqueDialog />
      <MerveilleDialog />
      <ForfaitsDialog />
      <CheckoutDialog />
      <DevisDialog />
    </>
  );
}
